import type { ChapterAccess, ChapterProgressState } from '@bookclub/shared';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { StatusBadge } from '@/shared/components/club/StatusBadge';

export interface ChapterRowItem {
  id: string;
  index: number;
  title: string;
  subtitle?: string | null;
  isUnpublished?: boolean;
  access: ChapterAccess;
  progressState?: ChapterProgressState;
}

const PROGRESS_ICONS = {
  not_started: null,
  in_progress: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--accent)]"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  completed: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-emerald-500"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
} as const;

const TITLE_CLAMP_STYLE = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

interface ChapterRowProps<T extends ChapterRowItem = ChapterRowItem> {
  chapter: T;
  onTap: (chapter: T) => void;
  active?: boolean;
  layout?: 'card' | 'list';
  showDivider?: boolean;
  showIndexBubble?: boolean;
}

export function ChapterRow<T extends ChapterRowItem>({
  chapter,
  onTap,
  active = false,
  layout = 'card',
  showDivider = false,
  showIndexBubble = true,
}: ChapterRowProps<T>) {
  const isLocked = chapter.access === 'locked';
  const progressState = chapter.progressState as keyof typeof PROGRESS_ICONS | undefined;
  const progressIcon = progressState ? PROGRESS_ICONS[progressState] : null;
  const circleClass =
    chapter.progressState === 'completed'
      ? 'bg-[rgba(93,205,121,0.18)] text-emerald-600'
      : active
        ? 'bg-[rgba(107,78,230,0.12)] text-[var(--primary)]'
        : 'bg-[rgba(26,26,26,0.08)] text-[rgba(26,26,26,0.62)]';

  if (layout === 'list') {
    return (
      <button onClick={() => onTap(chapter)} className="w-full text-left">
        <div
          className={[
            'flex items-center gap-3 rounded-[18px] px-2.5 py-1.5 transition-transform active:scale-[0.99] min-[430px]:gap-4 min-[430px]:px-3 min-[430px]:py-2',
            active ? 'bg-[rgba(107,78,230,0.05)]' : '',
            isLocked ? 'opacity-60' : '',
            showDivider ? 'border-b border-[rgba(26,26,26,0.08)] pb-4 min-[430px]:pb-5' : '',
          ].join(' ')}
        >
          {showIndexBubble ? (
            <div
              className={[
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-bold min-[430px]:h-11 min-[430px]:w-11',
                circleClass,
              ].join(' ')}
            >
              <span>{chapter.index.toString().padStart(2, '0')}</span>
            </div>
          ) : null}

          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-start gap-2">
              <p
                className="min-w-0 flex-1 text-[14px] font-semibold leading-[1.28] text-[var(--foreground)] min-[430px]:text-[14.5px]"
                style={TITLE_CLAMP_STYLE}
              >
                {chapter.title}
              </p>
              {chapter.isUnpublished ? (
                <StatusBadge
                  label="Неопубликовано"
                  tone="muted"
                  className="bg-[rgba(26,26,26,0.05)] text-[rgba(26,26,26,0.5)]"
                />
              ) : null}
            </div>
            {chapter.subtitle ? (
              <p className="truncate text-[11px] font-medium leading-[1.3] text-[rgba(26,26,26,0.48)] min-[430px]:text-[11.5px]">
                {chapter.subtitle}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            {chapter.access !== 'owned' ? (
              <StatusBadge
                status={chapter.access}
                className={chapter.access === 'demo' ? 'bg-[rgba(232,93,42,0.16)] text-[var(--accent)]' : ''}
              />
            ) : null}
            {active && chapter.progressState === 'in_progress' ? (
              <StatusBadge label="Продолжить" tone="primary" />
            ) : null}
            {chapter.progressState === 'completed' ? progressIcon : null}
            {isLocked ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[rgba(26,26,26,0.36)]"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            ) : null}
          </div>
        </div>
      </button>
    );
  }

  return (
    <button onClick={() => onTap(chapter)} className="w-full text-left">
      <MiniappSurface
        tone="muted"
        className={[
          'flex items-center gap-5 rounded-[22px] border-white/40 px-5 py-[1.15rem] shadow-none transition-transform active:scale-[0.99] min-[430px]:gap-6 min-[430px]:px-6 min-[430px]:py-[1.25rem]',
          isLocked ? 'opacity-60' : '',
          active ? 'border-[rgba(107,78,230,0.18)] ring-2 ring-[rgba(107,78,230,0.12)]' : '',
        ].join(' ')}
      >
        {showIndexBubble ? (
          <div
            className={[
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
              circleClass,
            ].join(' ')}
          >
            <span>{chapter.index.toString().padStart(2, '0')}</span>
          </div>
        ) : null}

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-start gap-2">
            <p
              className="min-w-0 flex-1 text-[14px] font-semibold leading-[1.3] text-[var(--foreground)] min-[430px]:text-[14.5px]"
              style={TITLE_CLAMP_STYLE}
            >
              {chapter.title}
            </p>
            {chapter.isUnpublished ? (
              <StatusBadge
                label="Неопубликовано"
                tone="muted"
                className="bg-[rgba(26,26,26,0.05)] text-[rgba(26,26,26,0.5)]"
              />
            ) : null}
          </div>
          {chapter.subtitle ? (
            <p className="truncate text-[11px] font-medium leading-[1.3] text-[rgba(26,26,26,0.48)] min-[430px]:text-[11.5px]">
              {chapter.subtitle}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {chapter.access !== 'owned' ? (
            <StatusBadge
              status={chapter.access}
              className={chapter.access === 'demo' ? 'bg-[rgba(232,93,42,0.16)] text-[var(--accent)]' : ''}
            />
          ) : null}
          {active && chapter.progressState === 'in_progress' ? (
            <StatusBadge label="Продолжить" tone="primary" />
          ) : null}
          {chapter.progressState === 'completed' ? progressIcon : null}
          {isLocked ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-[rgba(26,26,26,0.36)]"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          ) : null}
        </div>
      </MiniappSurface>
    </button>
  );
}
